import { useCallback, useEffect, useRef, useState } from 'react'
import styles from './BasicInfo.module.scss'
import BackButton from '../../components/buttons/BackButton/BackButton'
import { SideOpenedField } from '../../components/fields/SideOpenedField/SideOpenedField'
import { uploadS3, useAuthStatus } from '../../utils'
import { certificateNice } from '../../utils/certificateNice'
import { PasswordChangeModal } from '../../components/modal/PasswordChangeModal'
import { PhoneChangeModal } from '../../components/modal/PhoneChangeModal'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { CheckCircleIcon, ChevronRightIcon, PencilIcon } from '@heroicons/react/24/solid'
import { useNavigate, useLocation } from 'react-router-dom'
import { DeleteAccountModal } from './components/DeleteAccountModal'
import { useUser, useUpdateUser, useUpdateProfileImage } from '../../hooks'
import { useIsMobile } from '../../hooks'
import axios from 'axios'

// 약관 PDF 링크 (CDN)
const TERMS_CDN_BASE = 'https://dumzpdd1jp1uu.cloudfront.net'
const TERMS_LINK_PRIVACY = `${TERMS_CDN_BASE}/%E1%84%80%E1%85%A2%E1%84%8B%E1%85%B5%E1%86%AB%E1%84%8C%E1%85%A5%E1%86%BC%E1%84%87%E1%85%A9%E1%84%8E%E1%85%A5%E1%84%85%E1%85%B5%E1%84%87%E1%85%A1%E1%86%BC%E1%84%8E%E1%85%B5%E1%86%B7_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`
const TERMS_LINK_SERVICE = `${TERMS_CDN_BASE}/%E1%84%89%E1%85%A5%E1%84%87%E1%85%B5%E1%84%89%E1%85%B3%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%AD%E1%86%BC%E1%84%8B%E1%85%A3%E1%86%A8%E1%84%80%E1%85%AA%E1%86%AB_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`
const TERMS_LINK_ELECTRONIC = `${TERMS_CDN_BASE}/%E1%84%8C%E1%85%A5%E1%86%AB%E1%84%8C%E1%85%A1%E1%84%80%E1%85%B3%E1%86%B7%E1%84%8B%E1%85%B2%E1%86%BC%E1%84%80%E1%85%A5%E1%84%85%E1%85%A2%E1%84%83%E1%85%A9%E1%86%BC%E1%84%8B%E1%85%B4%E1%84%89%E1%85%A5_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`
const TERMS_LINK_MARKETING = `${TERMS_CDN_BASE}/%E1%84%86%E1%85%A1%E1%84%8F%E1%85%A6%E1%84%90%E1%85%B5%E1%86%BC%E1%84%89%E1%85%AE%E1%84%89%E1%85%B5%E1%86%AB%E1%84%83%E1%85%A9%E1%86%BC%E1%84%8B%E1%85%B4_%E1%84%89%E1%85%B3%E1%84%91%E1%85%A9%E1%84%8B%E1%85%B5%E1%84%8B%E1%85%B3%E1%86%B7_26.pdf`

export const BasicInfo = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const [auth, handleLogout] = useAuthStatus()
	const { data: user, isLoading } = useUser()
	const updateUserMutation = useUpdateUser()
	const updateProfileImageMutation = useUpdateProfileImage()
	const userId = auth.user?.id
	const isMobile = useIsMobile()

	const methods = useForm({
		defaultValues: {
			protection: true,
			useTerms: true,
			cash: true,
			message: user?.notification || false,
		},
	})
	const [isChanged, setIsChanged] = useState(false)
	const [verifiedStatus, setVerifiedStatus] = useState('notTried')
	const [info, setInfo] = useState({ name: '', phoneNumber: '' })
	const [verifyMessage, setVerifyMessage] = useState('')

	const handleCheck = (target) => {
		const current = methods.getValues(target)
		methods.setValue(target, !current)
		methods.setValue('fullTerms', false)
	}
	const popupRef = useRef(null)
	const [verifyingError, setVerifyingError] = useState('')
	const [isPasswordModalOpen, setPasswordModalOpen] = useState(false)
	const [isChangePhoneModalOpen, setChangePhoneModalOpen] = useState(false)
	const [isDeleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false)

	const fileInputRef = useRef(null)

	// useUser로부터 가져온 데이터 - 사이드바와 동일한 로직 사용
	const getDefaultProfileImage = (userType) => {
		switch (userType) {
			case 'tourCaddie':
				return '/images/caddie-default-profile.svg'
			case 'tourPro':
			default:
				return '/images/pro-default.png'
		}
	}

	const profileImage = user?.profile_image || getDefaultProfileImage(user?.userType)
	const name = user?.name || ''
	const email = user?.email || ''
	const phoneNumber = user?.phone || ''
	const handleImageChangeClick = () => {
		fileInputRef.current.click()
	}
	const onSubmit = async (data) => {
		console.log('data:', data)

		updateUserMutation.mutate(
			{
				name: data.name,
				phone: data.phoneNumber,
				notification: data.message,
				password: data.password,
			},
			{
				onSuccess: () => {
					if (!isPasswordModalOpen && !isChangePhoneModalOpen) {
						console.log('basicInfo에 있는 onSubmit 함수가 잘 실행되었다')
						navigate(-1)
					} else {
						setPasswordModalOpen(false)
						setChangePhoneModalOpen(false)
					}
				},
			},
		)
	}

	const handleVerificationSuccess = useCallback(
		(info) => {
			methods.setValue('name', info.name)
			methods.setValue('phoneNumber', info.phoneNumber)
			setIsChanged(true)
		},
		[methods.setValue],
	)

	const handleChangePassword = useCallback(async () => {
		console.log('verifiedStatus:', verifiedStatus)
		if (verifiedStatus !== 'success') {
			await handleVerificationNice()
		} else if (
			//만약 의존성 배열때문에 이 함수가 다시 실행 되었을때 실행할 조건문
			verifiedStatus === 'success' &&
			info.name === auth?.user.name &&
			info.phoneNumber === auth?.user.phone
		) {
			setVerifyingError('본인인증이 완료되었습니다.')
			setPasswordModalOpen(true)
		} else {
			setVerifyingError('개인정보가 일치하지 않습니다.')
			setVerifiedStatus('notTried')
		}
		/*
		if (verifiedStatus === 'notTried') {
			await handleVerificationNice()
			if (
				//만약 의존성 배열때문에 이 함수가 다시 실행 되었을때 실행할 조건문
				verifiedStatus === 'success' &&
				info.name === auth?.user.name &&
				info.phoneNumber === auth?.user.phone
			) {
				setPasswordModalOpen(true)
			}
		} else {
			console.log('개인정보 불일치 메세지 변수 설정은 됨')
			setVerifyingError('개인정보가 일치하지 않습니다.')
			setVerifiedStatus('notTried')
		}*/
	}, [verifiedStatus, setVerifyingError, setVerifiedStatus, setPasswordModalOpen])

	const handleClosePasswordModal = () => {
		setPasswordModalOpen(false)
	}

	const handleChangePhone = () => {
		setChangePhoneModalOpen(true)
	}

	const handleChangePhoneModal = () => {
		setChangePhoneModalOpen(false)
	}
	const handleDeleteAccountModal = () => {
		setDeleteAccountModalOpen(true)
	}
	const handleCloseDeleteAccountModal = () => {
		setDeleteAccountModalOpen(false)
	}

	const handleFileChange = async (event) => {
		const file = event.target.files[0]

		if (file) {
			// 1. 파일 확장자 유효성 검사 (JPG, PNG만 허용)
			const allowedTypes = ['image/jpeg', 'image/png']
			if (!allowedTypes.includes(file.type)) {
				alert('JPG 및 PNG 이미지 형식만 허용됩니다.')
				return
			}

			// 2. 파일 크기 유효성 검사 (5MB 이하만 허용)
			const maxSize = 5 * 1024 * 1024
			if (file.size > maxSize) {
				alert('파일 크기는 5MB 이하여야 합니다.')
				return
			}

			const userId = auth.user?.id

			try {
				// S3에 이미지 업로드
				const imageUrl = await uploadS3(userId, 'profile', file)

				if (imageUrl) {
					// Mutation으로 데이터베이스에 이미지 URL 저장
					updateProfileImageMutation.mutate(
						{ userId, imageUrl },
						{
							onSuccess: () => {
								console.log(
									'프로필 이미지가 데이터베이스에 성공적으로 저장되었습니다:',
									imageUrl,
								)
							},
							onError: (error) => {
								console.error('프로필 이미지 URL DB 저장 실패:', error)
								alert(
									'프로필 이미지 변경 중 오류가 발생했습니다. 다시 시도해주세요.',
								)
							},
						},
					)
				} else {
					console.error('S3 이미지 업로드 실패: imageUrl이 반환되지 않았습니다.')
					throw new Error('Failed to upload image to S3.')
				}
			} catch (error) {
				console.error('이미지 변경 중 오류 발생:', error)
				alert('프로필 이미지 변경 중 오류가 발생했습니다. 다시 시도해주세요.')
			}
		}
	}

	const NICE_CERT_STORAGE_KEY = 'niceCertificationResult'
	const NICE_CERT_START_KEY = 'niceCertificationStart'

	// 인증 결과 처리 함수
	const processCertificationResult = useCallback(
		(result) => {
			const { status, message: resultMessage, userInfo } = result

			if (status === 'success') {
				console.log('Nice 본인인증 최종 성공! 받은 데이터:', userInfo)
				const newInfo = {
					name: userInfo.name,
					phoneNumber: userInfo.phoneNumber, // Ensure this matches your backend's field name (phone vs phoneNumber)
				}
				setInfo(newInfo) // Update local state for display
				setVerifiedStatus('success') // Update status in parent for UI logic
				setVerifyMessage('본인인증에 성공했습니다.') // Display success message
				handleVerificationSuccess(newInfo) // Callback to parent to fill form fields
				console.log(newInfo)
				// console.log('auth:', auth)
				if (newInfo.name === auth.user?.name && newInfo.phoneNumber === auth.user?.phone) {
					setVerifyingError('본인인증이 완료된 상태입니다.')
					setPasswordModalOpen(true)
				} else {
					setVerifyingError('개인정보가 일치하지 않습니다.')
					setVerifiedStatus('notTried')
				}
			} else {
				console.error('Nice 본인인증 최종 실패:', resultMessage)
				setInfo({ name: '', phoneNumber: '' }) // Clear info on failure
				setVerifiedStatus('failed') // Update status in parent
				setVerifyMessage(`본인인증에 실패했어요: ${resultMessage}`) // Display failure message
			}
		},
		[
			setVerifiedStatus,
			handleVerificationSuccess,
			setPasswordModalOpen,
			auth.user?.name,
			auth.user?.phone,
		],
	)

	const handleMessageFromPopup = useCallback(
		(event) => {
			// 팝업인 경우에만 source 체크
			if (popupRef.current && event.source !== popupRef.current) return
			// IMPORTANT: Validate the origin for security!
			// This should match the exact domain of your parent page (where SelfVerification is rendered).
			// For local development, it might be 'http://localhost:3000'.
			// For production, it must be 'https://your-actual-domain.com'.
			// The popup (CertifyComplete.jsx) sends with window.location.origin.
			if (event.origin !== window.location.origin) {
				console.warn('Message received from unknown origin:', event.origin)
				return // Ignore messages from untrusted origins
			}

			// Check if it's the specific message type from your Nice certification complete page
			if (event.data && event.data.type === 'niceCertificationFinalResult') {
				processCertificationResult(event.data)
			}
		},
		[processCertificationResult],
	) // Dependencies for useCallback

	// sessionStorage 및 localStorage에서 인증 결과 확인 (새 창으로 이동한 경우)
	const checkStorageForResult = useCallback(() => {
		try {
			// sessionStorage 먼저 확인
			let storedResult = sessionStorage.getItem(NICE_CERT_STORAGE_KEY)
			if (!storedResult) {
				// sessionStorage에 없으면 localStorage 확인
				storedResult = localStorage.getItem(NICE_CERT_STORAGE_KEY)
			}

			if (storedResult) {
				const result = JSON.parse(storedResult)
				console.log('storage에서 인증 결과 발견:', result)
				processCertificationResult(result)
				// 처리 후 storage에서 제거
				sessionStorage.removeItem(NICE_CERT_STORAGE_KEY)
				localStorage.removeItem(NICE_CERT_STORAGE_KEY)
				localStorage.removeItem(NICE_CERT_START_KEY) // 인증 시작 정보도 제거
			}
		} catch (error) {
			console.error('storage에서 인증 결과 읽기 실패:', error)
		}
	}, [processCertificationResult])
	const handleVerificationNice = async () => {
		try {
			const currentPath = window.location.pathname
			const currentUrl = window.location.origin + currentPath // 전체 URL (origin + pathname)

			// 환경에 따라 returnURL 설정
			// - 웹: 클라이언트 /certify/complete (GET 파라미터로 처리)
			// - 모바일: 백엔드 서버 /certify/complete (POST로 처리 후 리다이렉트)
			let returnURL
			if (isMobile) {
				// 모바일: 백엔드 서버로 설정 (redirect 쿼리 파라미터에 전체 URL 포함)
				const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || window.location.origin
				returnURL = `${apiEndpoint}/users/certify/complete?redirect=${encodeURIComponent(currentUrl)}`
			} else {
				// 웹: 클라이언트로 설정
				returnURL = `${window.location.origin}/certify/complete`
			}

			console.log('시도중', { currentPath, currentUrl, returnURL, isMobile })

			// 인증 시작 정보를 localStorage에 저장 (모바일 앱 전환 대비)
			localStorage.setItem(
				NICE_CERT_START_KEY,
				JSON.stringify({
					timestamp: Date.now(),
					returnURL: returnURL,
					originalPath: currentPath, // 원래 페이지 경로 저장
				}),
			)

			popupRef.current = await certificateNice(returnURL, 'popupForPassword')
			console.log('certificateNice가 잘 실행되었다')
			// const result = await randomCheckVerification()

			// if (result.success) {
			// 	const newInfo = { name: result.name, phoneNumber: result.phoneNumber }
			// 	setInfo(newInfo)
			// 	changeVerifiedStatus('success')
			// 	setMessage('본인인증에 성공했습니다')
			// 	onVerificationSuccess(newInfo)
			// 	console.log('성공')
			// } else {
			// 	setInfo({ classNameame: '', phoneNumber: '' })
			// 	changeVerifiedStatus('failed')
			// 	setMessage('본인인증에 실패했어요')
			// }
		} catch (error) {
			setInfo({ name: '', phoneNumber: '' })
			setVerifiedStatus('failed')
			setVerifyMessage('인증 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.')
			console.error('인증 오류:', error)
		}
	}

	// URL 파라미터에서 인증 결과 처리 (모바일: 백엔드에서 리다이렉트된 결과만 처리)
	const processUrlParams = useCallback(async () => {
		const urlParams = new URLSearchParams(location.search)

		// 백엔드에서 리다이렉트된 결과 확인 (모바일)
		// 웹 환경은 CertifyComplete.js에서 처리하므로 여기서는 모바일 결과만 처리
		if (urlParams.get('cert') === 'complete') {
			const status = urlParams.get('status')
			console.log('백엔드에서 리다이렉트된 인증 결과 발견:', { status })

			if (status === 'success') {
				const name = urlParams.get('name')
				const phone = urlParams.get('phone')

				if (name && phone) {
					const newInfo = {
						name: decodeURIComponent(name),
						phoneNumber: phone,
					}

					// 결과 처리
					processCertificationResult({
						status: 'success',
						message: '본인인증이 성공적으로 완료되었습니다.',
						userInfo: newInfo,
					})

					// URL에서 파라미터 제거 (깔끔한 URL 유지)
					window.history.replaceState({}, '', location.pathname)

					// 인증 시작 정보 제거
					localStorage.removeItem(NICE_CERT_START_KEY)
					return
				}
			} else if (status === 'failed') {
				const message = urlParams.get('message')
				processCertificationResult({
					status: 'failed',
					message: message ? decodeURIComponent(message) : '본인인증에 실패했습니다.',
					userInfo: null,
				})

				// URL에서 파라미터 제거
				window.history.replaceState({}, '', location.pathname)
				localStorage.removeItem(NICE_CERT_START_KEY)
				return
			}
		}
		// 웹 환경: NICE URL 파라미터 처리는 CertifyComplete.js에서 수행하므로 여기서는 처리하지 않음
	}, [location.search, location.pathname, processCertificationResult])

	useEffect(() => {
		window.addEventListener('message', handleMessageFromPopup)
		return () => {
			window.removeEventListener('message', handleMessageFromPopup)
		}
	}, [handleMessageFromPopup])

	// 컴포넌트 마운트 시 및 페이지 포커스 시 storage 확인
	useEffect(() => {
		// URL 파라미터 먼저 확인 (Pass 앱에서 직접 돌아온 경우)
		processUrlParams()

		// 초기 마운트 시 확인
		checkStorageForResult()

		// 페이지 포커스 시 확인 (새 창에서 돌아왔을 때)
		const handleFocus = () => {
			processUrlParams()
			checkStorageForResult()
		}

		// visibilitychange 이벤트로도 확인 (탭 전환 시)
		const handleVisibilityChange = () => {
			if (!document.hidden) {
				// 약간의 지연을 두어 Pass 앱에서 돌아온 후 처리되도록
				setTimeout(() => {
					processUrlParams()
					checkStorageForResult()
				}, 300)
			}
		}

		// 주기적으로 확인 (모바일에서 앱 전환 후 돌아올 때를 대비)
		const checkInterval = setInterval(() => {
			// 인증 시작 정보가 있으면 결과를 확인
			const certStart = localStorage.getItem(NICE_CERT_START_KEY)
			if (certStart) {
				const startTime = JSON.parse(certStart).timestamp
				const now = Date.now()
				// 5분 이내의 인증 시도만 확인 (타임아웃 방지)
				if (now - startTime < 5 * 60 * 1000) {
					processUrlParams()
					checkStorageForResult()
				} else {
					// 타임아웃된 경우 정리
					localStorage.removeItem(NICE_CERT_START_KEY)
				}
			}
		}, 2000) // 2초마다 확인

		window.addEventListener('focus', handleFocus)
		document.addEventListener('visibilitychange', handleVisibilityChange)

		return () => {
			window.removeEventListener('focus', handleFocus)
			document.removeEventListener('visibilitychange', handleVisibilityChange)
			clearInterval(checkInterval)
		}
	}, [checkStorageForResult, processUrlParams])

	// user 데이터 로드 후 폼 초기화
	useEffect(() => {
		if (user) {
			methods.setValue('message', user.notification || false)
		}
	}, [user, methods])

	// 로딩 중일 때
	if (isLoading || !user) {
		return (
			<div className={styles.basicWrapper}>
				<BackButton />
				<div className={styles.basicForm}>
					<div className={styles.basicInfoTitleBox}>
						<h2 className={styles.basicInfoTitle}>기본 정보</h2>
						<span className={styles.explain}>로딩 중...</span>
					</div>
				</div>
			</div>
		)
	}

	return (
		<FormProvider {...methods}>
			<div className={styles.basicWrapper}>
				<BackButton />
				<form className={styles.basicForm} onSubmit={methods.handleSubmit(onSubmit)}>
					<div className={styles.basicInfoTitleBox}>
						<h2 className={styles.basicInfoTitle}>기본 정보</h2>

						<span className={styles.explain}> 계정, 연락처, 서비스 동의 등</span>
					</div>
					<div className={styles.headerImageContainer}>
						<div className={styles.profileImageWrapper}>
							<div className={styles.profileImageContainer}>
								<img
									src={profileImage}
									alt="프로필 이미지"
									className={styles.profileImage}
								/>
							</div>
							<div
								className={styles.changeImageContainer}
								onClick={handleImageChangeClick}>
								<PencilIcon className={styles.changeImage} />
							</div>
							<input
								type="file"
								ref={fileInputRef}
								style={{ display: 'none' }}
								accept="image/*"
								onChange={handleFileChange}
							/>
						</div>
					</div>
					<div className={styles.basicBox}>
						<SideOpenedField position="top" label="이름" value={name} />
						<SideOpenedField label="계정" value={email} />
						<SideOpenedField
							label="비밀번호"
							value={verifyingError}
							onAction={handleChangePassword}
							actionLabel="비밀번호 재설정"
						/>
						<SideOpenedField
							label="연락처"
							value={phoneNumber}
							onAction={handleChangePhone}
							actionLabel="변경"
						/>
						<div style={{ borderBottom: '1px solid #ECECEC' }}>
							<div>
								<input
									type="checkbox"
									id="protection"
									{...methods.register('protection', {
										required: '필수 약관입니다.',
									})}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`protection`)}
								/>
								<label htmlFor="protection" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>개인정보 처리 방침 </small>
										<a
											href={TERMS_LINK_PRIVACY}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<span className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('protection')
													? '#1B1B1B'
													: 'ECECEC',
											}}
										/>
									</span>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="useTerms"
									{...methods.register('useTerms', {
										required: '필수 약관입니다.',
									})}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`useTerms`)}
								/>
								<label htmlFor="useTerms" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>서비스 이용약관 </small>
										<a
											href={TERMS_LINK_SERVICE}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<span className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('useTerms')
													? '#1B1B1B'
													: 'ECECEC',
											}}
										/>
									</span>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="cash"
									{...methods.register('cash', { required: '필수 약관입니다.' })}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`cash`)}
								/>
								<label htmlFor="cash" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<small>전자금융거래 이용 동의서</small>
										<a
											href={TERMS_LINK_ELECTRONIC}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<small className={styles.right}>
										<CheckCircleIcon
											className="h-6 w-6 text-gray-500"
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('cash') ? '#1B1B1B' : 'ECECEC',
											}}
										/>
									</small>
								</label>
							</div>
							<div>
								<input
									type="checkbox"
									id="message"
									{...methods.register('message')}
									style={{ display: 'none' }}
									onChange={() => handleCheck(`message`)}
								/>
								<label htmlFor="message" className={styles.termsLabel}>
									<div className={styles.labelShow}>
										<div className={styles.labelShowInner}>
											<small>
												서비스 알림 수신 동의
												<span className="choice"> (선택) </span>
											</small>
											<div className={styles.comment}>
												(계약 제안·승인 등 중요한 알림이 실시간으로
												전달됩니다)
											</div>
										</div>
										<a
											href={TERMS_LINK_MARKETING}
											target="_blank"
											rel="noopener noreferrer"
											className={styles.showContent}
											onClick={(e) => e.stopPropagation()}>
											내용보기{' '}
											<ChevronRightIcon
												className="h-6 w-6 text-gray-500"
												style={{
													width: '10px',
													height: '10px',
												}}
											/>
										</a>
									</div>
									<small className={styles.right}>
										<CheckCircleIcon
											style={{
												width: '20px',
												height: '20px',
												color: methods.watch('message')
													? '#1B1B1B'
													: 'ECECEC',
											}}
										/>
									</small>
								</label>
							</div>
						</div>
						<div className={styles.belowBtns}>
							<button
								className={styles.outBtn}
								type="button"
								onClick={handleDeleteAccountModal}>
								회원탈퇴
							</button>
							<button
								className={styles.nextButton}
								type="submit"
								disabled={updateUserMutation.isPending}>
								{updateUserMutation.isPending ? '저장 중...' : '저장'}
							</button>
						</div>
					</div>
				</form>
				{isPasswordModalOpen && (
					<PasswordChangeModal
						isOpen={isPasswordModalOpen}
						onClose={handleClosePasswordModal}
						onSubmit={onSubmit}
					/>
				)}
				{isChangePhoneModalOpen && (
					<PhoneChangeModal
						isOpen={isChangePhoneModalOpen}
						onClose={handleChangePhoneModal}
						name={name}
						phoneNumber={phoneNumber}
						onSubmit={onSubmit}
					/>
				)}
				{isDeleteAccountModalOpen && (
					<DeleteAccountModal
						isOpen={isDeleteAccountModalOpen}
						onClose={handleCloseDeleteAccountModal}
					/>
				)}
			</div>
		</FormProvider>
	)
}
